import { Box, Button, Modal, Typography } from "@mui/material";
import PropTypes from "prop-types";
import { useMemo, useState } from "react";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 400,
  bgcolor: "background.paper",
  boxShadow: 24,
  borderRadius: 2,
  p: 4,
};

function ConfirmationModal({
  buttonLabel,
  loadingLabel,
  confirmLabel,
  cancelLabel,
  title,
  description,
  levelOrder,
  selectedQuestions,
  disabled,
  loading,
  onConfirm,
}) {
  const [open, setOpen] = useState(false);

  const questionsToDisplay = useMemo(
    () => selectedQuestions?.filter(Boolean) || [],
    [selectedQuestions]
  );

  const hasQuestions = questionsToDisplay.length > 0;

  const handleOpen = () => {
    if (disabled || !hasQuestions) {
      return;
    }
    setOpen(true);
  };

  const handleClose = () => setOpen(false);

  const handleConfirm = async () => {
    if (!onConfirm) {
      handleClose();
      return;
    }

    try {
      await onConfirm();
      handleClose();
    } catch (error) {
      console.error("Failed to confirm archive action:", error);
    }
  };

  return (
    <>
      <Button
        variant="outlined"
        color="error"
        onClick={handleOpen}
        disabled={disabled || loading}
      >
        {loading ? loadingLabel : buttonLabel}
      </Button>
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={style}>
          <Typography id="modal-modal-title" variant="h6" component="h2" sx={{ fontWeight: 'bold' }}>
            {title}
          </Typography>
          <Typography id="modal-modal-description" sx={{ mt: 2 }}>
            {description || `Level ${levelOrder} has ${questionsToDisplay.length} question${questionsToDisplay.length === 1 ? "" : "s"} selected for archival.`}
          </Typography>

          <Box component="ul" sx={{ mt: 2, pl: 3, mb: 2 }}>
            {questionsToDisplay.map((question) => {
              const trimmedText = (question.question || "Untitled question").trim();
              const readableText = trimmedText.length > 80 ? `${trimmedText.slice(0, 77)}...` : trimmedText;
              const orderLabel = Number.isFinite(question.order) ? question.order : "-";

              return (
                <Typography component="li" key={question._id} sx={{ mb: 0.5 }}>
                  {`Q${orderLabel}: ${readableText}`}
                </Typography>
              );
            })}
          </Box>

          <Typography variant="body2" color="warning.main" sx={{ mb: 2 }}>
            Archiving hides these questions from learners. You can restore them later if needed.
          </Typography>

          <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end", gap: 1 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleConfirm}
            >
              {confirmLabel}
            </Button>
            <Button variant="outlined" color="secondary" onClick={handleClose}>
              {cancelLabel}
            </Button>
          </Box>
        </Box>
      </Modal>
    </>
  );
}

ConfirmationModal.propTypes = {
  buttonLabel: PropTypes.string,
  loadingLabel: PropTypes.string,
  confirmLabel: PropTypes.string,
  cancelLabel: PropTypes.string,
  title: PropTypes.string,
  description: PropTypes.string,
  levelOrder: PropTypes.number,
  selectedQuestions: PropTypes.arrayOf(
    PropTypes.shape({
      _id: PropTypes.string.isRequired,
      order: PropTypes.number,
      question: PropTypes.string,
    })
  ),
  disabled: PropTypes.bool,
  loading: PropTypes.bool,
  onConfirm: PropTypes.func,
};

ConfirmationModal.defaultProps = {
  buttonLabel: "Archive Selected",
  loadingLabel: "Processing...",
  confirmLabel: "Confirm",
  cancelLabel: "Cancel",
  title: "Confirm Archive",
  description: "",
  levelOrder: undefined,
  selectedQuestions: [],
  disabled: false,
  loading: false,
  onConfirm: undefined,
};

export default ConfirmationModal;
